import React, { useEffect, useState, useMemo } from 'react'
import { Stat } from '@arnsbogroup/react-voicearchive'
import Feedback from './components/Feedback'
import { Full as Loading } from 'components/Loading'
import useApi from 'hooks/useApi'
import messages from "./messages";
import {FormattedMessage} from "react-intl";

const CustomerReportsPage = () => {
  const [customerId, setCustomerId] = useState(null)
  const [data, loadingData, dataErrors, refetchData] = useApi('/rest/v2/order/usage')
  const [customers, loadingCustomers, customersErrors, refetchCustomers] = useApi('/rest/v2/customer')
  const [customerFeedback, loadingFeedback, feedbackErrors, _, feedbackMeta] = useApi('/rest/v2/feedback')

  useEffect(() => {
    if (customers) {
      setCustomerId(customers[0].uid)
    }
  }, [customers])

  const customerNames = useMemo(() => {
    return customers ? customers.reduce((memo, { uid, name }) => {
      memo[uid] = name
      return memo
    }, {}) : {}
  }, [customers])

  const stats = [
    { name: 'Total # of orders', key: 'orders', icon: 'box-check' },
    { name: 'Total # of revisions', key: 'retakes', icon: 'repeat' },
    { name: 'Avg. turnaround time', key: 'turnaround', icon: 'hourglass-half' },
  ]

  const customerStats = useMemo(() => {
    if (!data || !customerId) {
      return {}
    }

    return data[customerId].stats
  }, [data, customerId])

  const feedback = useMemo(() => {
    if (!customerFeedback) {
      return []
    }

    return customerFeedback
      .filter((entry) => entry.customerId == customerId)
  }, [customerId, customerFeedback])

  return (
    <div className="py-3 px-4 lg:py-5 lg:px-6 space-y-6 w-full h-full flex flex-col">
      <div className="pb-5 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          <FormattedMessage{...messages.reports}/>
          <select className="text-lg leading-6 font-medium text-primary" onChange={({ target: { value } }) => setCustomerId(value)}>
            { Object.entries(customerNames).map(([id, name]) => (
              <option key={`options-${id}`} value={id}>{ name }</option>
            )) }
          </select>
        </h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500"><FormattedMessage{...messages.reportsExplanation}/></p>
      </div>

      { loadingData || loadingCustomers ? (
        <Loading />
      ) : dataErrors.length || customersErrors.length ? (
        <Loading>
          { dataErrors.join('') }
        </Loading>
      ) : (
        <React.Fragment>
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            { customerStats && stats.map(({ key, name, icon }) => (
              <Stat key={key} name={name} icon={icon} data={customerStats[key]} />
            ))}

            { feedbackMeta &&
              <Stat
                name="Average rating"
                icon="star"
                data={{
                  current: feedbackMeta.avg,
                  previous: feedbackMeta.avg,
                  change: feedbackMeta.avg
                }}
              />
            }

            { feedback &&
              <Stat
                name="Total # of feedback submissions"
                icon="letter"
                data={{
                  current: feedback.length,
                  previous: feedback.length,
                  change: feedback.length
                }}
              />
            }
          </dl>

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-3">
              <h2 className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                Feedback
              </h2>

              <div className="h-96 shadow rounded-sm">
                <Feedback feedback={feedback} />
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

export default CustomerReportsPage
